import type { HTMLAttributes } from "react";
import { cx } from "./cx";
import styles from "./Skeleton.module.css";

export type SkeletonVariant = "text" | "title" | "block" | "circle";

export type SkeletonProps = HTMLAttributes<HTMLDivElement> & {
  /** Forme attendue du contenu à venir. Défaut `text`. */
  variant?: SkeletonVariant;
  /** Nombre de lignes, pour `variant="text"`. Défaut `1`. */
  lines?: number;
  /** Largeur CSS explicite, ex. `"12rem"`. */
  width?: string;
  /** Hauteur CSS explicite, ex. `"8rem"`. */
  height?: string;
};

/**
 * Silhouette du contenu en cours de chargement.
 *
 * **Un écran qui charge montre un squelette, jamais un écran blanc** : la
 * silhouette dit déjà ce qui va arriver et où, si bien que l'arrivée des
 * données ne fait pas sauter la mise en page.
 *
 * L'ensemble est marqué `aria-hidden` et `aria-busy` : un lecteur d'écran n'a
 * rien à annoncer tant qu'il n'y a rien à lire — c'est à la zone parente de
 * porter le `aria-busy` si elle doit l'annoncer.
 */
export function Skeleton({
  variant = "text",
  lines = 1,
  width,
  height,
  className,
  style,
  ...rest
}: SkeletonProps) {
  const dimensions = { width, height, ...style };

  if (variant === "text" && lines > 1) {
    return (
      <div
        className={cx(styles.lignes, className)}
        style={{ width, ...style }}
        aria-hidden="true"
        {...rest}
      >
        {Array.from({ length: lines }, (_, i) => (
          <span key={i} className={cx(styles.squelette, styles.text)} />
        ))}
      </div>
    );
  }

  return (
    <div
      className={cx(styles.squelette, styles[variant], className)}
      style={dimensions}
      aria-hidden="true"
      {...rest}
    />
  );
}
